import { useState } from 'react'
import { Share2, Send, Instagram, Copy, Check, Loader2 } from 'lucide-react'
import { useShare, useTelegram } from '@/shared/lib/telegram'
import { cn } from '@/shared/lib/utils'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { apiClient } from '@/shared/api/client'

interface ShareButtonProps {
  /** Название товара */
  productName: string
  /** URL изображения для Stories */
  imageUrl?: string
  /** ID товара */
  productId: string
  /** Slug товара для ссылки */
  productSlug: string
  /** Тип товара */
  productType: 'tea' | 'teaware'
  /** Описание товара */
  productDescription?: string
  /** Цена товара */
  productPrice: number
  /** В наличии */
  isInStock?: boolean
  /** Размер кнопки */
  size?: 'sm' | 'md' | 'lg'
  /** Вариант отображения */
  variant?: 'icon' | 'button'
  /** Дополнительные классы */
  className?: string
}

const sizeClasses = {
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12',
}

const iconSizes = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
}

/**
 * Кнопка "Поделиться" для товаров
 * 
 * Использует shareMessage (Bot API 8.0+) для отправки
 * красивого сообщения с фото и кнопкой.
 */
export function ShareButton({
  productName,
  imageUrl,
  productId,
  productSlug,
  productType,
  productDescription,
  productPrice,
  isInStock = true,
  size = 'md',
  variant = 'icon',
  className,
}: ShareButtonProps) {
  const { shareToStory } = useShare()
  const { hapticFeedback, webApp } = useTelegram()
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Ссылка на товар через BotFather Direct Link Mini App.
  // Open in one click; miniapp reads start_param and routes to the product.
  const productLink = `https://t.me/drakonisoroka_bot/shop?startapp=${productType}_${productSlug}`
  
  const handleOpenSheet = () => {
    hapticFeedback.impactOccurred('light')
    setError(null)
    setIsSheetOpen(true)
  }
  
  const handleShareToStory = () => {
    if (!imageUrl) return
    
    const storyText = productType === 'tea' 
      ? `🍵 ${productName} — открыл для себя этот чай в Дракон и Сорока`
      : `🫖 ${productName} — нашёл в чайном клубе Дракон и Сорока`
    
    shareToStory({
      mediaUrl: imageUrl,
      text: storyText,
      widgetLink: {
        // Direct link to the shop mini-app — tapping the story sticker opens the
        // product directly via start_param (no bot intermediate screen).
        url: `https://t.me/drakonisoroka_bot/shop?startapp=${productType}_${productSlug}`,
        name: 'Чайный клуб',
      },
    })
    setIsSheetOpen(false)
  }
  
  /**
   * Отправить другу — красивое сообщение с фото и кнопкой
   * 
   * Использует shareMessage (Bot API 8.0+):
   * 1. Бэкенд создаёт prepared message через savePreparedInlineMessage
   * 2. Фронтенд вызывает webApp.shareMessage(preparedMessageId)
   * 3. Telegram показывает диалог выбора чата с превью сообщения
   * 4. Пользователь выбирает чат и отправляет
   */
  const handleSendToFriend = async () => {
    // Проверяем поддержку shareMessage
    if (typeof (webApp as any).shareMessage !== 'function') {
      setError('Для красивой отправки нужна новая версия Telegram')
      hapticFeedback.notificationOccurred('error')
      return
    }
    
    setIsSharing(true)
    setError(null)
    hapticFeedback.impactOccurred('light')
    
    try {
      // Получаем preparedMessageId от бэкенда
      const response = await apiClient<{ preparedMessageId: string; success: boolean; error?: string }>(
        '/api/user/share/prepare',
        {
          method: 'POST',
          body: JSON.stringify({
            productType,
            productId,
            productName,
            productSlug,
            productDescription,
            productPrice,
            productImageUrl: imageUrl,
            isInStock,
          }),
          initData: 'true',
        }
      )
      
      if (!response.success || !response.preparedMessageId) {
        throw new Error(response.error || 'Не удалось подготовить сообщение')
      }
      
      // Закрываем sheet ПЕРЕД вызовом shareMessage
      // Диалог Telegram откроется поверх приложения
      setIsSheetOpen(false)
      setIsSharing(false)
      
      // Вызываем shareMessage — откроется диалог выбора чата
      // с красивым превью сообщения (фото + текст + кнопка)
      ;(webApp as any).shareMessage(response.preparedMessageId, (sent: boolean) => {
        if (sent) {
          hapticFeedback.notificationOccurred('success')
        }
        // Если не отправлено — пользователь сам закрыл диалог, это нормально
      })
      
    } catch (err) {
      console.error('[Share] Error:', err)
      setError('Не удалось поделиться')
      hapticFeedback.notificationOccurred('error')
      setIsSharing(false)
    }
  }
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(productLink)
      hapticFeedback.notificationOccurred('success')
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      hapticFeedback.notificationOccurred('error')
    }
  }
  
  return (
    <>
      {variant === 'icon' ? (
        <button
          type="button"
          onClick={handleOpenSheet}
          className={cn(
            'flex items-center justify-center rounded-full',
            'bg-white/80 dark:bg-dark-card/80 backdrop-blur-sm',
            'border border-silk/50 dark:border-dark-border/50',
            'text-gray-600 dark:text-gray-400',
            'hover:bg-white dark:hover:bg-dark-card',
            'active:scale-95 transition-all',
            'shadow-sm',
            sizeClasses[size],
            className
          )}
          aria-label="Поделиться"
        >
          <Share2 className={iconSizes[size]} />
        </button>
      ) : (
        <button
          type="button"
          onClick={handleOpenSheet}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-xl',
            'bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-gray-300',
            'hover:bg-gray-200 dark:hover:bg-dark-elevated',
            'active:scale-95 transition-all',
            'text-sm font-medium',
            className
          )}
        >
          <Share2 className="w-4 h-4" />
          <span>Поделиться</span>
        </button>
      )}
      
      <BottomSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        title="Поделиться"
        fitContent
      >
        <div className="px-4 pb-8 space-y-2">
          {/* Ошибка */}
          {error && (
            <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}
          
          {/* Отправить другу — красивое сообщение */}
          <button
            type="button"
            onClick={handleSendToFriend}
            disabled={isSharing}
            className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-dark-elevated transition-colors text-left disabled:opacity-50"
          >
            <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
              {isSharing ? (
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              ) : (
                <Send className="w-5 h-5 text-white" />
              )}
            </div>
            <div className="flex-1">
              <div className="font-medium text-ink dark:text-white">
                {isSharing ? 'Готовлю...' : 'Порекомендовать другу'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {isSharing ? 'Создаю красивую карточку' : 'Отправить с фото и описанием'}
              </div>
            </div>
          </button>
          
          {/* Stories */}
          {imageUrl && (
            <button
              type="button"
              onClick={handleShareToStory}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-dark-elevated transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Instagram className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-ink dark:text-white">
                  Рассказать в Stories
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Поделиться находкой с подписчиками
                </div>
              </div>
            </button>
          )}
          
          {/* Скопировать ссылку */}
          <button
            type="button"
            onClick={handleCopyLink}
            className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-dark-elevated transition-colors text-left"
          >
            <div className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center transition-colors',
              copied ? 'bg-green-500' : 'bg-gray-200 dark:bg-dark-elevated'
            )}>
              {copied ? (
                <Check className="w-5 h-5 text-white" />
              ) : (
                <Copy className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              )}
            </div>
            <div className="flex-1">
              <div className="font-medium text-ink dark:text-white">
                {copied ? 'Готово!' : 'Скопировать ссылку'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {copied ? 'Теперь можно вставить куда угодно' : 'Отправить где удобно'}
              </div>
            </div>
          </button>
        </div>
      </BottomSheet>
    </>
  )
}
